import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  TextField,
  Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import { getLoans, type LoanSummary } from '../../endpoints/loans';

const LoansTestList: React.FC = () => {
  const [userFilter, setUserFilter] = useState('');
  const navigate = useNavigate();

  const params = userFilter.trim() ? { user_id: userFilter.trim() } : undefined;
  const {
    data: loans,
    isLoading,
    error,
  } = useApiGet<LoanSummary[]>(
    ['loans', params ?? 'all'],
    () => getLoans(params),
    { enabled: true }
  );

  const list = loans ?? [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Loans (Test / Verification)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create loans, add payments/charges, and run due-date processing to
          verify calculations. Requires loan:manage permission.
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          size="small"
          label="Filter by User ID"
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
          fullWidth
          placeholder="Leave empty for your loans"
        />
      </Grid>
      <Grid item xs={12}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/administration/loans-test/create')}
          sx={{ mb: 2 }}
        >
          Create loan
        </Button>
      </Grid>
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          {isLoading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          )}
          {error && (
            <Box p={2}>
              <Typography color="error">
                {(error as any)?.response?.data?.error ??
                  (error as Error).message}
              </Typography>
            </Box>
          )}
          {!isLoading && !error && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Principal</TableCell>
                  <TableCell align="right">Interest</TableCell>
                  <TableCell align="right">Charges</TableCell>
                  <TableCell>Next payment</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9}>No loans found.</TableCell>
                  </TableRow>
                ) : (
                  list.map((loan) => (
                    <TableRow key={loan.id} hover>
                      <TableCell
                        sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                      >
                        {loan.id.slice(0, 8)}…
                      </TableCell>
                      <TableCell
                        sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                      >
                        {loan.user_id.slice(0, 8)}…
                      </TableCell>
                      <TableCell align="right">
                        {loan.amount.toFixed(2)}
                      </TableCell>
                      <TableCell>{loan.status}</TableCell>
                      <TableCell align="right">
                        {loan.current_principal_balance.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {loan.current_interest_balance.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {loan.current_charges_balance.toFixed(2)}
                      </TableCell>
                      <TableCell>{loan.next_payment_date ?? '—'}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() =>
                            navigate(`/administration/loans-test/${loan.id}`)
                          }
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default LoansTestList;
